import { inkAsset, inkPart } from "./ink-assets";
import { jointPose, type Joint, type Motion, type Facing } from "./motion";
import type { ArtState } from "./living-art";
import { drawBotanicalActor } from "./botanical-rig";
const tau = Math.PI * 2;
/** Direction-specific attachment coordinates shared by renderer, overlay and audits. */
export function inkPose(
  id: string,
  motion: Motion,
  phase: number,
  facing: Facing = "east",
) {
  const p = jointPose(motion, phase),
    front = facing === "south",
    back = facing === "north",
    moving = ["walk", "run", "start", "stop"].includes(motion);
  if (!front && !back)
    for (const [side, shift] of [
      ["near", -28],
      ["far", 26],
    ] as const)
      for (const point of [
        `${side}Shoulder`,
        `${side}Elbow`,
        `${side}Hand`,
      ] as const)
        p[point][0] += shift;
  if (id === "pumpkin" && front)
    for (const [side, shift] of [
      ["near", 5],
      ["far", -5],
    ] as const)
      for (const point of [
        `${side}Shoulder`,
        `${side}Elbow`,
        `${side}Hand`,
      ] as const)
        p[point][0] += shift;
  const hips = { nearHip: [...p.hip] as Joint, farHip: [...p.hip] as Joint };
  for (const [side, sign] of [
    ["near", 1],
    ["far", -1],
  ] as const) {
    const root = hips[`${side}Hip`],
      foot = p[`${side}Foot`],
      separation = front || back ? sign * 10 : sign * 2;
    root[0] += separation;
    foot[0] += separation;
    if (front || back) {
      foot[0] = root[0];
      foot[1] += sign * Math.cos(phase * tau) * (moving ? 4 : 0);
    }
    const dx = foot[0] - root[0],
      dy = foot[1] - root[1],
      d = Math.max(0.001, Math.hypot(dx, dy)),
      h = Math.sqrt(Math.max(0, 19 * 19 - (d * d) / 4)),
      bend = front || back ? sign : 1;
    p[`${side}Knee`] = [
      (root[0] + foot[0]) / 2 + ((bend * dy) / d) * h,
      (root[1] + foot[1]) / 2 - ((bend * dx) / d) * h,
    ];
  }
  return { ...p, ...hips };
}
function bone(
  ctx: CanvasRenderingContext2D,
  id: string,
  a: Joint,
  b: Joint,
  length: number,
  shade = false,
) {
  ctx.save();
  if (shade) ctx.globalAlpha *= 0.83;
  const angle = Math.atan2(b[1] - a[1], b[0] - a[0]) - Math.PI / 2;
  inkPart(ctx, id, a[0], a[1], length, 0.5, 0.08, angle);
  ctx.restore();
}
/** All sample actors use painted head, torso, limbs and tools, on fixed-length joints. */
export function drawInkActor(
  ctx: CanvasRenderingContext2D,
  id: string,
  s: ArtState,
): boolean {
  if (drawBotanicalActor(ctx, id, s)) return true;
  if (!["hero", "pumpkin", "mite"].includes(id)) return false;
  const group = id === "mite" ? "pumpkin" : id;
  const required =
    id === "mite"
      ? ["beetle-shell", "beetle-head", "beetle-upper", "beetle-lower"]
      : [
          "head-front",
          "head-back",
          "head-side",
          "body-front",
          "body-back",
          "body-side",
          "upper-arm",
          "forearm",
          "thigh",
          "boot",
        ];
  if (!required.every((n) => inkAsset(`${group}/${n}`))) return false;
  ctx.save();
  if (s.facing === "west") ctx.scale(-1, 1);
  const phase = s.phase,
    time = s.time ?? phase * 2,
    back = s.facing === "north",
    front = s.facing === "south",
    moving = ["walk", "run", "start", "stop", "rush"].includes(s.motion);
  if (id === "mite") {
    const drawLeg = (i: number) => {
      const side = i < 3 ? -1 : 1,
        offset = (i % 3) * 0.333 + (side < 0 ? 0.5 : 0),
        p = (phase + offset) % 1;
      const travel = moving
          ? (p < 0.5 ? 1 - 4 * p : -1 + 4 * (p - 0.5)) * 15
          : 0,
        lift = moving && p > 0.5 ? Math.sin((p - 0.5) * tau) * 9 : 0;
      const root: Joint = [side * 12, -34 + (i % 3) * 4],
        foot: Joint = [
          side * (32 + (i % 3) * 5) + travel,
          -lift + ((i % 3) - 1) * 3,
        ];
      const dx = foot[0] - root[0],
        dy = foot[1] - root[1],
        d = Math.hypot(dx, dy),
        h = Math.sqrt(Math.max(0, 28 * 28 - (d * d) / 4));
      const joint: Joint = [
        (root[0] + foot[0]) / 2 + ((side * dy) / d) * h,
        (root[1] + foot[1]) / 2 - ((side * dx) / d) * h,
      ];
      bone(ctx, "pumpkin/beetle-upper", root, joint, 31, i < 3);
      bone(ctx, "pumpkin/beetle-lower", joint, foot, 32, i < 3);
      if (s.audit) {
        ctx.strokeStyle = i < 3 ? "#20d8ef" : "#e955bd";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(...root);
        ctx.lineTo(...joint);
        ctx.lineTo(...foot);
        ctx.stroke();
      }
    };
    for (let i = 0; i < 3; i++) drawLeg(i);
    if (back) inkPart(ctx, "pumpkin/beetle-head", 0, -39, 30, 0.5, 1, Math.PI);
    inkPart(ctx, "pumpkin/beetle-shell", 0, -8, 59, 0.5, 1, 0);
    for (let i = 3; i < 6; i++) drawLeg(i);
    if (!back)
      inkPart(
        ctx,
        "pumpkin/beetle-head",
        front
          ? 0
          : 26 + (s.motion === "attack" ? Math.sin(phase * Math.PI) * 9 : 0),
        front ? 0 : -19,
        32,
        0.5,
        1,
        front ? 0.65 : 0,
      );
    if (s.audit) {
      ctx.strokeStyle = "#ee62c4";
      ctx.lineWidth = 1;
      ctx.strokeRect(-48, -70, 96, 73);
    }
    ctx.restore();
    return true;
  }
  const p = inkPose(id, s.motion, phase, s.facing),
    hero = id === "hero",
    view = back ? "back" : front ? "front" : "side",
    prefix = `${id}/`;
  const leg = (near: boolean) => {
    const k = near ? p.nearKnee : p.farKnee,
      f = near ? p.nearFoot : p.farFoot,
      root = near ? p.nearHip : p.farHip;
    bone(ctx, prefix + "thigh", root, k, 23, !near);
    const boot = inkAsset(prefix + "boot")!,
      size = 23,
      width = (size * boot.width) / boot.height;
    // The calf turns at the knee, while the sole remains level on contact.
    ctx.save();
    if (!near) ctx.globalAlpha *= 0.83;
    ctx.translate(k[0], k[1]);
    ctx.rotate(Math.atan2(f[1] - 5 - k[1], f[0] - k[0]) - Math.PI / 2);
    ctx.drawImage(
      boot,
      0,
      0,
      boot.width,
      boot.height * 0.74,
      -width * 0.5,
      -2,
      width,
      size * 0.74,
    );
    ctx.restore();
    ctx.save();
    if (!near) ctx.globalAlpha *= 0.83;
    ctx.drawImage(
      boot,
      0,
      boot.height * 0.72,
      boot.width,
      boot.height * 0.28,
      f[0] - width * 0.5,
      f[1] - size * 0.28,
      width,
      size * 0.28,
    );
    ctx.restore();
  };
  const arm = (near: boolean, hand = true) => {
    const a = near ? p.nearShoulder : p.farShoulder,
      e = near ? p.nearElbow : p.farElbow,
      h = near ? p.nearHand : p.farHand;
    bone(ctx, prefix + "upper-arm", a, e, 24, !near);
    if (hand) bone(ctx, prefix + "forearm", e, h, 23, !near);
  };
  leg(false);
  leg(true);
  arm(false);
  if (hero)
    inkPart(
      ctx,
      "hero/scarf",
      p.shoulder[0] - 7,
      p.shoulder[1] + 2,
      35,
      0.8,
      0.08,
      Math.sin(time * 2.4) * 0.06 + (moving ? 0.12 : 0),
    );
  inkPart(ctx, prefix + "body-" + view, p.hip[0], p.hip[1] + 9, hero ? 56 : 74);
  if (front) arm(false);
  const nod =
    s.motion === "observe"
      ? Math.sin(phase * tau) * 0.07
      : s.motion === "hurt"
        ? -0.12 * Math.sin(phase * Math.PI)
        : Math.sin(time * 1.4) * 0.016;
  inkPart(
    ctx,
    prefix + "head-" + view,
    p.shoulder[0] + (back ? 0 : 1),
    p.shoulder[1] + (hero ? 6 : -3),
    hero ? 38 : 46,
    0.5,
    1,
    nod,
  );
  arm(true, false);
  const h = p.nearHand,
    tool =
      s.motion === "chop"
        ? "axe"
        : s.motion === "mine"
          ? "pick"
          : s.motion === "craft"
            ? "axe"
            : s.motion === "water"
              ? "can"
              : hero
                ? "staff"
                : "axe";
  if (
    !["plant", "gather", "harvest", "eat", "rest", "social"].includes(s.motion)
  ) {
    const angle = ["chop", "mine", "craft"].includes(s.motion)
      ? -1.5 + Math.sin(phase * Math.PI) * 2.5
      : s.motion === "attack"
        ? Math.sin(phase * Math.PI) * 1.2
        : hero
          ? 0.08
          : 0.22;
    inkPart(
      ctx,
      `${tool === "pick" || tool === "can" ? "hero" : id}/${tool}`,
      h[0] + 3,
      h[1],
      tool === "can" ? 26 : hero ? 75 : 70,
      tool === "can" ? 0.4 : 0.48,
      tool === "can" ? 0.25 : 0.66,
      angle,
    );
  }
  bone(ctx, prefix + "forearm", p.nearElbow, p.nearHand, 23);
  if (s.motion === "eat") {
    ctx.fillStyle = "#b7803f";
    ctx.beginPath();
    ctx.ellipse(h[0], h[1], 5, 4, 0, 0, tau);
    ctx.fill();
  }
  if (["cast", "heal", "water"].includes(s.motion)) {
    ctx.strokeStyle = s.motion === "water" ? "#7199aa" : "#a3bd81";
    ctx.lineWidth = 1.5;
    for (let i = 0; i < 5; i++) {
      const a = (i * tau) / 5 + phase * 3;
      ctx.beginPath();
      ctx.ellipse(
        h[0] + Math.cos(a) * 23,
        h[1] - 18 + Math.sin(a) * 20,
        2,
        3,
        a,
        0,
        tau,
      );
      ctx.stroke();
    }
  }
  if (s.audit)
    for (const side of ["far", "near"] as const) {
      ctx.strokeStyle = side === "far" ? "#19d7ef" : "#ed4aab";
      ctx.lineWidth = 1.4;
      for (const points of [
        [p[`${side}Hip`], p[`${side}Knee`], p[`${side}Foot`]],
        [p[`${side}Shoulder`], p[`${side}Elbow`], p[`${side}Hand`]],
      ]) {
        ctx.beginPath();
        points.forEach((v, i) => (i ? ctx.lineTo(...v) : ctx.moveTo(...v)));
        ctx.stroke();
      }
    }
  if (s.audit)
    for (const [name, j] of Object.entries(p)) {
      ctx.fillStyle = name.startsWith("far") ? "#19d7ef" : "#ed4aab";
      ctx.beginPath();
      ctx.arc(j[0], j[1], 2, 0, tau);
      ctx.fill();
    }
  ctx.restore();
  return true;
}
const PROP_MAP: Record<string, string> = {
  tree: "tree",
  rock: "rock",
  amber: "amber",
  herb: "herb",
  berry: "berry",
  well: "well",
  gate: "gate",
  camp: "camp",
  "crop-pumpkin": "crop-pumpkin",
  "crop-carrot": "crop-carrot",
  "decor-fern": "fern",
  "decor-flowers": "herb",
  "decor-log": "log",
  "decor-root": "log",
  "decor-ruin": "spent-rock",
  "decor-stones": "spent-rock",
  "decor-reed": "fern",
  "decor-shroom": "log",
};
/** Root stays fixed. Static ink linework is combined with independently animated local leaves/sparkles. */
export function drawInkObject(
  ctx: CanvasRenderingContext2D,
  id: string,
  s: ArtState,
): boolean {
  if (id.startsWith("crop-") && inkAsset(`v8/${id}/mature`)) {
    const growth = s.motion === "grow" ? s.phase : 1;
    const stage =
      growth < 0.25 ? "seedling" : growth < 0.72 ? "young" : "mature";
    const heights = { seedling: 25, young: 48, mature: 74 };
    ctx.save();
    if (s.motion === "harvest") ctx.globalAlpha *= 1 - s.phase;
    inkPart(ctx, `v8/${id}/${stage}`, 0, 5, heights[stage]);
    const t = s.time ?? 0;
    if (growth > 0.25)
      for (let i = 0; i < 2; i++)
        inkPart(
          ctx,
          `v8/${id}/leaf`,
          (i ? 1 : -1) * 12,
          -heights[stage] * 0.33,
          15,
          0.5,
          0.96,
          (i ? 1 : -1) * 0.5 + Math.sin(t * 1.1 + i * 2.2) * 0.09,
        );
    ctx.restore();
    return true;
  }
  const decorPart: Record<string, string> = {
    "decor-fern": "seedling",
    "decor-reed": "young",
    "decor-shroom": "mature",
    "decor-flowers": "leaf",
  };
  if (decorPart[id] && inkAsset(`v8/decor/${decorPart[id]}`)) {
    inkPart(
      ctx,
      `v8/decor/${decorPart[id]}`,
      0,
      3,
      id === "decor-reed" ? 48 : 34,
    );
    return true;
  }
  let key = PROP_MAP[id];
  if (!key) return false;
  const depleted = s.active === false || s.motion === "depleted";
  if (depleted)
    key =
      id === "tree"
        ? "stump"
        : ["rock", "amber"].includes(id)
          ? "spent-rock"
          : "fern";
  const im = inkAsset("props/" + key);
  if (!im) return false;
  const height =
    id === "tree"
      ? depleted
        ? 41
        : 144
      : id === "gate"
        ? 127
        : id === "well"
          ? 108
          : id === "camp"
            ? 106
            : id.startsWith("decor-")
              ? 35
              : id.startsWith("crop-")
                ? 69
                : 80;
  ctx.save();
  if (s.motion === "harvest") ctx.globalAlpha *= 1 - s.phase;
  inkPart(ctx, "props/" + key, 0, 5, height);
  if (
    !depleted &&
    (id === "tree" ||
      id === "herb" ||
      id === "berry" ||
      id.startsWith("crop-") ||
      id.startsWith("decor-"))
  ) {
    const time = s.time ?? s.phase * 2;
    for (let i = 0; i < (id === "tree" ? 5 : 3); i++) {
      const x = (i - (id === "tree" ? 2 : 1)) * (id === "tree" ? 17 : 12),
        y =
          id === "tree"
            ? -78 - Math.sin(i * 1.7) * 22
            : -18 - Math.sin(i * 2) * 9;
      const bend = Math.sin(time * 1.15 + i * 2.2 + x * 0.021) * 0.1;
      if (inkAsset("pumpkin/leaf"))
        inkPart(
          ctx,
          "pumpkin/leaf",
          x,
          y,
          id === "tree" ? 13 : 9,
          0.5,
          0.92,
          bend,
        );
    }
  }
  if (s.motion === "hurt" && !depleted) {
    ctx.strokeStyle = "#725c41";
    ctx.lineWidth = 1.1;
    for (let i = 0; i < 5; i++) {
      const a = i * 2.4;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * 10, -25 + Math.sin(a) * 5);
      ctx.lineTo(
        Math.cos(a) * (14 + s.phase * 27),
        -25 + Math.sin(a) * (9 + s.phase * 27),
      );
      ctx.stroke();
    }
  }
  if ((id === "rock" || id === "amber") && !depleted) {
    ctx.globalAlpha *= 0.25 + Math.max(0, Math.sin((s.time ?? 0) * 1.5)) * 0.5;
    ctx.strokeStyle = id === "rock" ? "#e7f4ee" : "#fff0ab";
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(3, -65);
    ctx.lineTo(5, -49);
    ctx.moveTo(0, -54);
    ctx.lineTo(9, -54);
    ctx.stroke();
    if ((s.health ?? 1) < 0.6) {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = "#645a54";
      ctx.beginPath();
      ctx.moveTo(-7, -47);
      ctx.lineTo(4, -32);
      ctx.lineTo(-2, -18);
      ctx.stroke();
    }
  }
  ctx.restore();
  return true;
}
